/**
 * Le hasard des dés — et la garantie qu'il n'est pas biaisé.
 *
 * Au 421 le résultat d'un lancer est décidé **ici**, pas par la physique 3D : le
 * dé qui roule à l'écran est une mise en scène d'un résultat déjà tiré (voir
 * docs/PLAN-V2.md § 7.1). C'est donc ce fichier, et lui seul, qui détermine
 * l'équité du jeu.
 *
 * Deux pièges classiques, tous deux évités ici :
 *
 * 1. **Le biais modulo.** `random() % 6` sur une source de 8 bits favorise les
 *    petites valeurs, parce que 256 n'est pas divisible par 6 : les valeurs 0 et 1
 *    sortiraient 43 fois sur 256 contre 42 pour les autres. On rejette donc la
 *    queue non divisible (rejection sampling) au lieu de replier avec un modulo.
 * 2. **`Math.random()` côté serveur.** Non cryptographique et prévisible. Réservé
 *    aux tests et aux animations.
 *
 * `tests/hasard.test.ts` vérifie l'uniformité par un test du khi².
 */
/** Source cryptographique standard, disponible côté Node comme navigateur. */
export const sourceCrypto = (taille) => {
    const tampon = new Uint8Array(taille);
    globalThis.crypto.getRandomValues(tampon);
    return tampon;
};
/**
 * Construit un tireur non biaisé à partir d'une source d'octets.
 *
 * 252 = 6 × 42 : on ne garde que les octets strictement inférieurs à 252, ce qui
 * rend les six faces exactement équiprobables. La probabilité de rejeter un octet
 * est de 4/256, soit 1,6 % — le coût est négligeable.
 */
export function creerTireur(source = sourceCrypto) {
    const LIMITE = 252;
    return (nombre) => {
        const des = [];
        while (des.length < nombre) {
            // On demande un peu plus que nécessaire pour limiter les allers-retours.
            const lot = source(Math.max(4, (nombre - des.length) * 2));
            for (const octet of lot) {
                if (octet >= LIMITE)
                    continue; // queue non divisible par 6 : rejetée
                des.push(((octet % 6) + 1));
                if (des.length === nombre)
                    break;
            }
        }
        return des;
    };
}
/** Tireur de production. */
export const tireurCrypto = creerTireur();
export function tireurScripte(faces) {
    let i = 0;
    const tireur = Object.assign((nombre) => {
        const out = faces.slice(i, i + nombre);
        if (out.length < nombre) {
            throw new Error(`séquence de dés épuisée : ${nombre} demandés, ${out.length} restants`);
        }
        i += nombre;
        return out;
    }, { reste: () => faces.length - i });
    return tireur;
}
/** Tire trois dés et renvoie une main (l'ordre des dés est conservé). */
export function tirerMain(tireur) {
    const [a, b, c] = tireur(3);
    return [a, b, c];
}
//# sourceMappingURL=hasard.js.map